import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { downloadXlsx } from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Download, Layers, FileSpreadsheet, Save, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronRight } from "lucide-react";

export default function AnnualReport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [role, setRole] = useState("all");
  const [openDept, setOpenDept] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [ika, setIka] = useState("");
  const [ikaM, setIkaM] = useState({});
  const { data: years } = useQuery({
    queryKey: ["report-years"],
    queryFn: async () => (await api.get("/report/years")).data,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["annual", year],
    queryFn: async () => (await api.get(`/report/annual?year=${year}`)).data,
  });
  const { data: compare } = useQuery({
    queryKey: ["report-compare", (years || []).join(",")],
    queryFn: async () => (await api.get(`/report/compare?years=${years.join(",")}`)).data,
    enabled: !!years?.length,
  });
  useEffect(() => {
    if (data) { setIka(String(data.ika_total ?? 0)); setIkaM(data.ika_months || {}); }
  }, [data, year]);
  const ikaMonthsSum = Object.values(ikaM).reduce((s, v) => s + (Number(v) || 0), 0);

  const saveIka = useMutation({
    mutationFn: async () => (await api.put("/report/ika", {
      year, total: ikaMonthsSum > 0 ? Math.round(ikaMonthsSum * 100) / 100 : Number(ika) || 0,
      months: Object.fromEntries(Array.from({ length: 12 }, (_, i) => [String(i + 1), Number(ikaM[String(i + 1)]) || 0])),
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["annual"] });
      qc.invalidateQueries({ queryKey: ["report-compare"] });
      qc.invalidateQueries({ queryKey: ["budget-compare"] });
      toast.success("Το ΙΚΑ του έτους αποθηκεύτηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (isLoading || !data) return <div className="text-[#6F6A60] py-10 text-center">Φόρτωση ετήσιας αναφοράς…</div>;

  const months = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const roles = ["all", ...(data.role_totals || []).map((r) => r.name)];
  const rows = data.rows.filter((r) => role === "all" || (r.department || "—") === role);

  const exportCsv = () => {
    const header = ["Όνομα", "Τμήμα", ...months.map((m) => MONTHS_EL_SHORT[Number(m)]), "Πάσχα", "Άδεια", "Χριστούγεννα", "Αποδοχές", "ΦΜΥ", "ΕΦΚΑ", "Εργοδ. ΙΚΑ", "Κόστος", "TAF", "TRIBE"];
    const lines = rows.map((r) =>
      [r.name, r.department, ...months.map((m) => r.months?.[m] || 0), r.easter, r.leave, r.christmas, r.gross ?? 0, r.tax ?? 0, r.efka ?? 0, r.ika, r.total, r.taf_cost, r.tribe_cost]
        .map((v) => `"${v}"`).join(",")
    );
    const blob = new Blob(["\uFEFF" + [header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `TAF_Etisia_Anafora_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="annual-report-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">{year} · Πλήρες Έτος</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Ετήσια Αναφορά Κόστους</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Μισθοδοσία ανά υπάλληλο, δώρα, ΦΜΥ, ΕΦΚΑ, εργοδοτικό ΙΚΑ, συνολικό κόστος και κατανομή TAF / TRIBE.
            {data.source === "payroll" ? " Πηγή: μισθοδοσίες έτους." : " Πηγή: ετήσιος πίνακας πρόβλεψης."}
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex gap-1.5" data-testid="report-year-chips">
            {(years || [year]).map((yv) => (
              <button key={yv} data-testid={`report-year-${yv}`} onClick={() => setYear(yv)}
                className={`rounded-xl border px-3 py-2.5 text-sm font-semibold tabular transition-colors ${
                  yv === year ? "border-[#45423C] bg-[#45423C] text-white" : "border-[#E3DED4] bg-white text-[#45423C] hover:border-[#B9AE9A]"}`}>
                {yv}
              </button>
            ))}
          </div>
          <select data-testid="report-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
            {roles.map((r) => <option key={r} value={r}>{r === "all" ? "Όλοι οι ρόλοι" : r}</option>)}
          </select>
          <button data-testid="export-xlsx-btn" onClick={() => downloadXlsx(`/export/annual.xlsx?year=${year}`, `TAF_Etisia_Anafora_${year}.xlsx`)} className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            <FileSpreadsheet className="h-4 w-4" /> Εξαγωγή Excel
          </button>
          <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
            <Download className="h-4 w-4" /> Εξαγωγή CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="card-gross">
          <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Μικτές Αποδοχές</div>
          <div className="font-serif text-2xl text-[#45423C] mt-1 tabular">{fmtEuro(data.extra_totals.gross)}</div>
        </div>
        {!data.extra_totals.efka && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="card-ika">
          <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Εργοδοτικό ΙΚΑ</div>
          <div className="font-serif text-2xl text-[#45423C] mt-1 tabular">{fmtEuro(data.extra_totals.ika)}</div>
          {data.ika_estimated && <div className="text-[10px] text-[#9A6A3A] mt-1" data-testid="ika-estimated-note">εκτίμηση — καταχωρίστε τα εκκαθαριστικά ΕΦΚΑ παρακάτω</div>}
        </div>
        )}
        <div className="rounded-2xl bg-[#C86235] text-white p-5" data-testid="card-cost">
          <div className="text-[10px] uppercase tracking-wider text-white/70">Συνολικό Κόστος</div>
          <div className="font-serif text-2xl mt-1 tabular">{fmtEuro(data.extra_totals.total)}</div>
          <div className="text-[10px] text-white/70 mt-1" data-testid="cost-formula">{data.extra_totals.efka ? "αποδοχές + ΦΜΥ + ΕΦΚΑ" : "αποδοχές + ΦΜΥ + εργοδοτικό ΙΚΑ"}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="card-tax-efka">
          <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">ΦΜΥ / ΕΦΚΑ (εκκαθαριστικά)</div>
          <div className="font-serif text-xl text-[#45423C] mt-1 tabular">{fmtEuro(data.extra_totals.tax)} <span className="text-[#B9AE9A]">/</span> {fmtEuro(data.extra_totals.efka)}</div>
          <div className="text-[10px] text-[#8A8577] mt-1">{data.extra_totals.tax || data.extra_totals.efka ? "από εισαγωγή ΦΜΥ/ΕΦΚΑ" : "δεν έχουν εισαχθεί — Μισθοδοσία → Εισαγωγή ΦΜΥ/ΕΦΚΑ"}</div>
        </div>
        <div className="rounded-2xl bg-[#45423C] text-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-[#B9AE9A]">Κόστος TAF</div>
          <div className="font-serif text-2xl mt-1 tabular">{fmtEuro(data.extra_totals.taf_cost)}</div>
        </div>
        <div className="rounded-2xl bg-[#6E7A63] text-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-white/60">Κόστος TRIBE</div>
          <div className="font-serif text-2xl mt-1 tabular">{fmtEuro(data.extra_totals.tribe_cost)}</div>
        </div>
      </div>

      {!data.extra_totals.efka && <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="ika-card">
        <div>
          <h2 className="text-lg font-medium text-[#45423C]">Εισφορές ΙΚΑ {year} — εκκαθαριστικά ΕΦΚΑ ανά μήνα</h2>
          <p className="text-xs text-[#6F6A60] mt-1">
            Γράψτε το πραγματικό εργοδοτικό ΙΚΑ κάθε μήνα από το εκκαθαριστικό ΕΦΚΑ. Κατανέμεται στους υπαλλήλους ανάλογα με τις αποδοχές του μήνα. Μήνες χωρίς ποσό υπολογίζονται στον Προϋπολογισμό και το P&L με εκτίμηση 25%.
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2">
          {months.map((m) => (
            <label key={m} className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{MONTHS_EL_SHORT[Number(m)]}</span>
              <input data-testid={`ika-month-${m}`} type="number" step="0.01" min="0" value={ikaM[m] || ""} disabled={!canEdit} placeholder="—"
                onChange={(e) => setIkaM({ ...ikaM, [m]: e.target.value })}
                className="w-full rounded-lg border border-[#E3DED4] px-2 py-2 text-xs tabular text-right outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
            </label>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{ikaMonthsSum > 0 ? "Σύνολο μηνών (€)" : "Ή μόνο ετήσιο σύνολο (€)"}</span>
            <input data-testid="ika-input" type="number" step="0.01" min="0" value={ikaMonthsSum > 0 ? Math.round(ikaMonthsSum * 100) / 100 : ika} disabled={!canEdit || ikaMonthsSum > 0}
              onChange={(e) => setIka(e.target.value)}
              className="w-40 rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm tabular outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
          </label>
          {canEdit && (
            <button data-testid="ika-save" onClick={() => saveIka.mutate()} disabled={saveIka.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
              <Save className="h-4 w-4" /> Αποθήκευση
            </button>
          )}
        </div>
      </div>}

      {compare?.years?.length > 1 && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="year-compare">
          <div className="px-5 py-3 border-b border-[#E3DED4] flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium text-[#45423C]">Σύγκριση Ετών ανά Τμήμα</h2>
            <span className="text-xs text-[#6F6A60] tabular">
              {compare.years.map((yv) => `${yv}: ${fmtEuro(compare.totals[yv])}`).join("  ·  ")}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-[#F1EDE6] text-left uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-2.5">Τμήμα</th>
                  {compare.years.map((yv, i) => (
                    <React.Fragment key={yv}>
                      <th className="px-3 py-2.5 text-right">{yv}</th>
                      {i > 0 && <th className="px-3 py-2.5 text-right">Δ%</th>}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compare.rows.map((r) => (
                  <React.Fragment key={r.department}>
                  <tr data-testid={`compare-row-${r.department}`} onClick={() => setOpenDept(openDept === r.department ? null : r.department)}
                    className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] cursor-pointer">
                    <td className="px-4 py-2 font-medium text-[#45423C]"><span className="inline-flex items-center gap-1.5">{openDept === r.department ? <ChevronDown className="h-3.5 w-3.5 text-[#C86235]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#B9AE9A]" />}{r.department} <span className="text-[10px] text-[#8A8577]">({(r.employees || []).length})</span></span></td>
                    {compare.years.map((yv, i) => {
                      const ch = r.changes?.[yv];
                      return (
                        <React.Fragment key={yv}>
                          <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{r.values[yv] ? fmtEuro(r.values[yv]) : "–"}</td>
                          {i > 0 && (
                            <td className={`px-3 py-2 text-right tabular font-semibold ${ch == null ? "text-[#B9AE9A]" : ch > 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>
                              {ch == null ? "–" : (
                                <span className="inline-flex items-center gap-1">
                                  {ch > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                                  {Math.abs(ch).toFixed(1)}%
                                </span>
                              )}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  {openDept === r.department && (r.employees || []).map((e) => (
                    <tr key={e.name} data-testid={`compare-emp-${r.department}-${e.name}`} className="bg-[#FAF8F4] text-xs">
                      <td className="px-4 py-1.5 pl-10 text-[#45423C]">{e.name}{e.position && e.position !== r.department ? <span className="text-[#8A8577]"> · {e.position}</span> : null}</td>
                      {compare.years.map((yv, i) => {
                        const cur = e.values?.[yv], prev = i > 0 ? e.values?.[compare.years[i - 1]] : null;
                        const ch = cur && prev ? (cur - prev) / prev * 100 : null;
                        return (
                          <React.Fragment key={yv}>
                            <td className="px-3 py-1.5 text-right tabular text-[#6F6A60]">{cur ? fmtEuro(cur) : "–"}</td>
                            {i > 0 && <td className={`px-3 py-1.5 text-right tabular ${ch == null ? "text-[#B9AE9A]" : ch > 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{ch == null ? "–" : `${ch > 0 ? "+" : ""}${ch.toFixed(1)}%`}</td>}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                  <td className="px-4 py-3">ΣΥΝΟΛΟ</td>
                  {compare.years.map((yv, i) => {
                    const ch = compare.total_changes?.[yv];
                    return (
                      <React.Fragment key={yv}>
                        <td className="px-3 py-3 text-right tabular">{fmtEuro(compare.totals[yv])}</td>
                        {i > 0 && (
                          <td className={`px-3 py-3 text-right tabular ${ch == null ? "text-[#B9AE9A]" : ch > 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>
                            {ch == null ? "–" : `${ch > 0 ? "+" : "−"}${Math.abs(ch).toFixed(1)}%`}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5">
        <div className="flex items-center gap-2 mb-4"><Layers className="h-4 w-4 text-[#C86235]" /><h2 className="text-lg font-medium text-[#45423C]">Σύνολα ανά Ρόλο / Τμήμα</h2></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(data.role_totals || []).map((r) => (
            <button key={r.name} onClick={() => setRole(r.name)} data-testid={`role-total-${r.name}`}
              className={`text-left rounded-xl border p-3 transition-colors ${role === r.name ? "border-[#B9AE9A] bg-[#B9AE9A]/5" : "border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
              <div className="text-xs text-[#6F6A60] truncate">{r.name}</div>
              <div className="font-serif text-lg text-[#45423C] tabular">{fmtEuro(r.value)}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-[#45423C] text-left uppercase tracking-wider text-white/80">
                <th className="px-3 py-3 sticky left-0 bg-[#45423C] z-10">Υπάλληλος</th>
                <th className="px-3 py-3">Τμήμα</th>
                {months.map((m) => <th key={m} className="px-2 py-3 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                <th className="px-2 py-3 text-right">Πάσχα</th>
                <th className="px-2 py-3 text-right">Άδεια</th>
                <th className="px-2 py-3 text-right">Χρ/να</th>
                <th className="px-2 py-3 text-right">Αποδοχές</th>
                <th className="px-2 py-3 text-right">ΦΜΥ</th>
                <th className="px-2 py-3 text-right">ΕΦΚΑ</th>
                {!data.extra_totals.efka && <th className="px-2 py-3 text-right">Εργοδ. ΙΚΑ</th>}
                <th className="px-3 py-3 text-right">Κόστος</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id ?? r.employee_id ?? `${r.name}-${idx}`} data-testid={`report-row-${idx}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-3 py-2 font-medium text-[#45423C] sticky left-0 bg-white z-10">{r.name}</td>
                  <td className="px-3 py-2 text-[#6F6A60]">{r.department}</td>
                  {months.map((m) => (
                    <td key={m} className="px-2 py-2 text-right tabular text-[#6F6A60]">
                      {(r.months?.[m] || 0) ? fmtEuro(r.months[m]).replace("€", "") : "–"}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right tabular">{r.easter ? fmtEuro(r.easter).replace("€", "") : "–"}</td>
                  <td className="px-2 py-2 text-right tabular">{r.leave ? fmtEuro(r.leave).replace("€", "") : "–"}</td>
                  <td className="px-2 py-2 text-right tabular">{r.christmas ? fmtEuro(r.christmas).replace("€", "") : "–"}</td>
                  <td className="px-2 py-2 text-right tabular font-medium">{fmtEuro(r.gross ?? 0).replace("€", "")}</td>
                  <td className="px-2 py-2 text-right tabular text-[#6F6A60]">{r.tax ? fmtEuro(r.tax).replace("€", "") : "–"}</td>
                  <td className="px-2 py-2 text-right tabular text-[#6F6A60]">{r.efka ? fmtEuro(r.efka).replace("€", "") : "–"}</td>
                  {!data.extra_totals.efka && <td className="px-2 py-2 text-right tabular text-[#C86235]">{r.ika ? fmtEuro(r.ika).replace("€", "") : "–"}</td>}
                  <td className="px-3 py-2 text-right tabular font-semibold text-[#45423C]">{fmtEuro(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {role === "all" && (
              <tfoot>
                <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                  <td className="px-3 py-3 sticky left-0 bg-[#F1EDE6] z-10">ΣΥΝΟΛΟ</td>
                  <td />
                  {months.map((m) => <td key={m} className="px-2 py-3 text-right tabular">{fmtEuro(data.column_totals[m]).replace("€", "")}</td>)}
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.easter).replace("€", "")}</td>
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.leave).replace("€", "")}</td>
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.christmas).replace("€", "")}</td>
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.gross).replace("€", "")}</td>
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.tax).replace("€", "")}</td>
                  <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.efka).replace("€", "")}</td>
                  {!data.extra_totals.efka && <td className="px-2 py-3 text-right tabular">{fmtEuro(data.extra_totals.ika).replace("€", "")}</td>}
                  <td className="px-3 py-3 text-right tabular">{fmtEuro(data.extra_totals.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
